/**
 * Banner informativo de FRETE GRÁTIS — faixa no topo do cardápio e no checkout.
 * Só aparece se a loja ligou (window.FREE_SHIPPING.banner_enabled). Texto: o que
 * o lojista escreveu, ou automático pelo mínimo (global+cap-km no km/raio, ou
 * nomeando os bairros no modo bairro). Sólido (sem gradiente) e quebra linha.
 *
 * IMPORTANTE: NÃO renderizar dentro do HeaderIsland — fazer isso quebrou a
 * interação dos modais do app inteiro (2 cliques/fantasma). Na home, é montado
 * num root próprio (#free-shipping-banner-root). No checkout, inline.
 */
import { useEffect, useState } from 'react';
import { t, useI18n } from '../i18n';

interface FreeShippingGlobals {
  FREE_SHIPPING?: { banner_enabled?: boolean; banner_text?: string; global_min?: number; max_km?: number; enabled?: boolean };
  FEE_TYPE?: string;
  STORE_NEIGHBORHOODS?: Array<{ name?: string; free_shipping_min?: number | null }>;
}

function brl(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function titleCase(s: string): string {
  const lower = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);
  return s.trim().toLowerCase().split(/\s+/)
    .map((w, i) => (i > 0 && lower.has(w)) ? w : w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function bannerText(): string | null {
  const w = window as unknown as FreeShippingGlobals;
  const fs = w.FREE_SHIPPING || {};
  if (!fs.banner_enabled) return null;
  if (fs.banner_text && fs.banner_text.trim()) return fs.banner_text.trim();

  const feeType = w.FEE_TYPE || 'neighborhood';
  if (feeType === 'km' || feeType === 'radius') {
    const min = Number(fs.global_min) || 0;
    if (!fs.enabled || min <= 0) return null;
    const maxKm = Number(fs.max_km) || 0;
    const area = maxKm > 0
      ? t('até {km} km', { km: maxKm.toLocaleString('pt-BR') })
      : t('em toda a área');
    return t('Frete grátis {area} para pedidos acima de {valor}', { area, valor: brl(min) });
  }
  const elig = (w.STORE_NEIGHBORHOODS || [])
    .map((n) => ({ name: titleCase(String(n.name || '')), min: Number(n.free_shipping_min) || 0 }))
    .filter((n) => n.name && n.min > 0);
  if (elig.length === 0) return null;
  const minSet = Array.from(new Set(elig.map((n) => n.min)));
  if (minSet.length === 1) {
    const names = elig.map((n) => n.name);
    const list = names.length <= 3
      ? names.join(', ').replace(/, ([^,]*)$/, ` ${t('e')} $1`)
      : t('{lista} e mais {n} bairros', { lista: names.slice(0, 2).join(', '), n: names.length - 2 });
    return t('Frete grátis para {lista} acima de {valor}', { lista: list, valor: brl(minSet[0]) });
  }
  const parts = elig.slice(0, 3).map((n) => t('{bairro} acima de {valor}', { bairro: n.name, valor: brl(n.min) }));
  const extra = elig.length > 3 ? ` +${elig.length - 3}` : '';
  return t('Frete grátis: {lista}', { lista: parts.join(' · ') }) + extra;
}

export function FreeShippingBanner({ variant = 'top' }: { variant?: 'top' | 'inline' }) {
  // A frase é montada por `bannerText()` FORA do React — o hook aqui é o que
  // faz ela ser remontada no idioma novo quando o cliente troca a bandeira.
  const { lang } = useI18n();
  const [text, setText] = useState<string | null>(() => bannerText());
  useEffect(() => {
    const refresh = () => setText(bannerText());
    refresh();
    window.addEventListener('aliv:home-rendered', refresh);
    const timers = [setTimeout(refresh, 400), setTimeout(refresh, 1200), setTimeout(refresh, 2500)];
    return () => {
      window.removeEventListener('aliv:home-rendered', refresh);
      timers.forEach(clearTimeout);
    };
  }, [lang]);
  if (!text) return null;
  return (
    <div
      style={{ backgroundColor: '#059669' }}
      className={
        'flex items-start justify-center gap-2 text-white text-[13px] font-extrabold px-3 py-2 tracking-tight ' +
        (variant === 'top' ? 'shadow-md' : 'rounded-xl shadow')
      }
    >
      <i className="fas fa-truck-fast text-white shrink-0 mt-0.5" />
      <span className="text-center leading-snug break-words">{text}</span>
    </div>
  );
}
